/// <amd-module name="Test/TestPlansHubRefresh/Pivots/AuthorPivot/TestCaseCSVExporterUtility" />
import { WorkItemColumnOptions, isCustomField, getCustomFieldName } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/ColumnOptionsCommonUtils";
import * as AuthorResources from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/Resources";
import { TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { checkIfIsIE } from "Test/TestPlansHubRefresh/TestHub/Util";

/**
 * Dictionary for indentation level
 */
export interface IIndentationsData {
    maxIndentation: number;
    indentationsMap: { [id: number]: number };
}

/**
 * Test Case data object in csv format
 */
export interface ITestCaseData {
    rows: string[][];
    testCaseIds: number[];
    columns: string[];
}

/**
 * Utility to export test case object to CSV
 */
export class TestCaseCSVExporterUtility {
    private c_newlineChar = "\n";
    private c_commaDelimiter = ",";
    private csvFileName: string;
    private testCaseData: ITestCaseData;

    constructor(fileName: string) {
        this.csvFileName = fileName
    }

    public formatTestCaseData(testCasesData: any, testCaseIds: number[]): ITestCaseData {
        let convertedData = {
            testCaseIds: testCaseIds,
            rows: [],
            columns: []
        } as ITestCaseData;
        convertedData.testCaseIds = testCaseIds;

        const result = this._populateColumns(testCasesData);
        convertedData.columns = result['columns'];
        const referenceNames = result['referenceNames'];

        convertedData.rows = this._populateRows(testCasesData, referenceNames);
        this.testCaseData = convertedData;
        this.downloadCSV();
        return this.testCaseData;
    }

    public downloadCSV(): void {
        const fileName = this.csvFileName;
        this._getCSVContent(this.testCaseData)
            .then(data => {
                if (data && data.length > 0) {
                    const blob = new Blob(["\ufeff" + data], { type: "text/csv;charset=utf-8;" });
                    if (checkIfIsIE()) {
                        (window.navigator as any).msSaveBlob(blob, this.csvFileName); // IE11 support
                    } else {
                        const a = window.document.createElement("a");
                        const blobUrl = window.URL.createObjectURL(blob);
                        a.href = blobUrl;
                        a.download = fileName;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(blobUrl); // Dispose blob
                    }
                }
            })
            .catch(err => {
                if (err) {
                    console.error(err);
                }
            });
    }

    private _populateColumns(testCasesData: any) {
        let columns = [];
        let referenceNames = [];
        // Id is displayed first
        columns.push(AuthorResources.Id)
        columns.push(AuthorResources.WorkItemType);
        columns.push(AuthorResources.Title);
        columns.push(AuthorResources.TestStep);
        columns.push(AuthorResources.StepAction);
        columns.push(AuthorResources.StepExpected);
        
        let additionalFields = Object.keys(testCasesData[0].additionalFields);
        for (let i = 0; i < additionalFields.length; i++) {
            if (WorkItemColumnOptions[additionalFields[i]] && additionalFields[i] != TestHubRefreshConstants.WorkItemField_Id) {
                // add WIT field column names
                columns.push(WorkItemColumnOptions[additionalFields[i]].name);
                referenceNames.push(additionalFields[i]);
            }
            else if (isCustomField(additionalFields[i])) {
                // add custom field column names
                columns.push(additionalFields[i]);
                referenceNames.push(additionalFields[i]);
            }
        }
        const result = {
            'columns': columns,
            'referenceNames': referenceNames
        }
        return result;
    }

    private _populateRows(testCasesData: any, referenceNames: string[]) {
        let rows: string[][] = [];
        for (let i = 0, testCasesLength = testCasesData.length; i < testCasesLength; i++) {
            let row = [];
            if (testCasesData[i].isSharedStep) {
                row.push(String(testCasesData[i].refId));
                row.push(AuthorResources.SharedStepWorkItemType);
            }
            else if (testCasesData[i].isTestCase) {
                row.push(String(testCasesData[i].additionalFields[TestHubRefreshConstants.WorkItemField_Id]));
                row.push(AuthorResources.TestCaseWorkItemType);
            }
            else {
                row.push("");
                row.push("");
            }
            row.push(String(testCasesData[i].testCaseTitle));
            row.push(testCasesData[i].actionId != 0 ? String(testCasesData[i].actionId): "");
            row.push(String(testCasesData[i].action));
            row.push(String(testCasesData[i].expectedResult));

            for (let j = 0, referenceNamesLength = referenceNames.length; j < referenceNamesLength; j++) {
                if (testCasesData[i].isTestCase) {
                    row.push(testCasesData[i].additionalFields[referenceNames[j]] ?
                        String(testCasesData[i].additionalFields[referenceNames[j]]): "");
                } else {
                    row.push("");
                }
            }
            rows[i] = row;
        }

        return rows;
    }

    private _getCSVContent(testCaseData: ITestCaseData): Promise<string> {
        return new Promise((resolve, reject) => {
            const successCallback = (testCasesResultModel: any) => {
                if (testCaseData && testCaseData.testCaseIds && testCaseData.testCaseIds.length > 0) {
                    this._getCSVContentHelper(testCaseData)
                        .then(csv => {
                            resolve(csv);
                        })
                        .catch(err => {
                            reject(err);
                        });
                } else {
                    resolve("");
                }
            };

            // Use what's cached on the client if possible because it reflects the results present in the UI
            if (testCaseData) {
                successCallback(testCaseData);
            } else {
            }
        });
    }

    private async _getCSVContentHelper(testCasesResultModel: ITestCaseData): Promise<string> {
        const testCaseIds = this._deduplicate(testCasesResultModel.testCaseIds);

        const promises: any = [this._getEscapedCSVRows(this.testCaseData.rows)];

        const csvRowsArr: any = await Promise.all(promises);

        csvRowsArr.unshift([testCasesResultModel.columns]);
        const indexToSplitOn = 0;
        const indentationsData: IIndentationsData = this._getIndentationsData(testCaseIds, []);

        const csvTexts = this._getCSVTextsFromRows(csvRowsArr, indexToSplitOn, indentationsData);

        // Return filtered, concatenated CSV string
        return Promise.resolve(csvTexts.filter(csvText => csvText.length > 0).join(this.c_newlineChar));
    }

    private _deduplicate(arr: number[]): number[] {
        const result: any = [];
        const hasVisitedDict: any = {};

        arr.forEach(elem => {
            if (!hasVisitedDict[elem]) {
                hasVisitedDict[elem] = true;
                result.push(elem);
            }
        });

        return result;
    }

    // Construct the CSV string
    private _getCSVTextsFromRows(csvRowsArr: string[], indexToSplitOn: number, indentationsData: IIndentationsData): string[] {
        const maxIndentation = indentationsData.maxIndentation;

        const csvTexts = csvRowsArr.map((rows, index) => {
            if (rows.length === 0) {
                return "";
            }

            let csvText = "";

            // Form the CSV string while reconciling the different indentations with the maximum one
            for (let i = 0, rowLength = rows.length; i < rowLength; i++) {
                const isColHeaderRow = index == 0 && i == 0;
                const indentation = 0;

                // Append 'prefix'
                for (let j = 0; j < indexToSplitOn; j++) {
                    csvText += rows[i][j] + this.c_commaDelimiter;
                }

                // Handle indentation
                for (let j = 0; j <= maxIndentation; j++) {
                    if (isColHeaderRow) {
                        csvText += rows[i][indexToSplitOn] + (maxIndentation > 0 ? " " + String(j + 1) : ""); // ie. Title 1, Title 2, ...
                    } else if (j === indentation) {
                        csvText += rows[i][indexToSplitOn]; // Place field value in correct space
                    }

                    csvText += j < maxIndentation || indexToSplitOn < rows[i].length - 1 ? this.c_commaDelimiter : "";
                }

                // Append 'suffix'
                for (let j = indexToSplitOn + 1; j < rows[i].length; j++) {
                    csvText += rows[i][j] + (j < rows[i].length - 1 ? this.c_commaDelimiter : "");
                }

                csvText += i < rows.length - 1 ? this.c_newlineChar : "";
            }

            return csvText;
        });
        return csvTexts;
    }

    // Creates dictionary for indentation level - assumes that testCaseIds has no duplicates
    private _getIndentationsData(testCaseIds: number[], sourceIds: number[]): IIndentationsData {
        const indentationsData: IIndentationsData = {
            maxIndentation: 0,
            indentationsMap: {}
        };

        testCaseIds.forEach((targetId, index) => {
            let indentation = 0;
            if (sourceIds.length > 0 && sourceIds[index] !== 0) {
                indentation = indentationsData.indentationsMap[sourceIds[index]] + 1; // Must exist due to tree structure
            }
            indentationsData.indentationsMap[targetId] = indentation;
            indentationsData.maxIndentation = Math.max(indentationsData.maxIndentation, indentation);
        });

        return indentationsData;
    }

    private _getEscapedCSVRows(rows: any[]): string[] {
        if (!rows || rows.length === 0) {
            return [];
        }

        const result: any = [];

        for (let i = 0; i < rows.length; i++) {
            if (!(rows[i] instanceof Array)) {
                continue; // Could not find test case due to invalid ID.
            }

            const escapedRow = new Array(rows[i].length);
            for (let j = 0; j < rows[i].length; j++) {
                escapedRow[j] = this._escapeForCSV(rows[i][j]);
            }

            result.push(escapedRow);
        }

        return result;
    }

    private _escapeForCSV(obj: any): string {
        let result = "";

        // Do we want to convert booleans to 1/0?
        if (typeof obj === typeof true) {
            result = obj ? "1" : "0";
        } else {
            // If obj is a datetime string, use local time and use "d/m/yyyy hh:mm:ss AM/PM" format (for example: 1/7/2020 11:33:29 AM)
            if (obj instanceof Date) {
                const dateobj = obj as Date;

                // Not using toLocaleString() because it adds a comma between the date and string.
                // Excel will automatically identify this date time format if there is no comma, making it easier for the user to modify the date formatting within Excel.
                obj = `${dateobj.toLocaleDateString()} ${dateobj.toLocaleTimeString()}`;
            }
            const str = obj == null ? "" : String(obj).replace(/"/g, '""');
            if (str !== "") {
                result += `\"${str}\"`;
            }
        }

        return result;
    }
}
